/**
 * DocumentSeenByList — "seen by" panel for a document.
 *
 * Lists users who have viewed or downloaded the document, with per-user
 * view/download counts and the last-seen relative time. The backend only
 * returns data to the uploader, the workspace owner, or an admin (and
 * re-checks on every request); this component simply renders whatever the
 * API returns and hides itself if access is denied or the list is empty.
 *
 * Type-normalized by the backend: file/voice come from DocumentView,
 * text messages from the existing read-receipts (MessageRead).
 *
 * Props:
 *   - documentId
 *   - canView   (parent's optimistic gate — uploader/owner/admin; backend re-checks)
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { ar as arLocale } from 'date-fns/locale'
import { HiOutlineEye, HiOutlineArrowDownTray, HiOutlineUsers } from 'react-icons/hi2'
import { documentAPI } from '../../services/api'
import { useDirection } from '../../contexts/DirectionContext'
import { Card, Badge } from '../ui'

function initials(name) {
  if (!name) return '?'
  const parts = String(name).trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?'
}

function unwrap(res) {
  return res?.data?.data ?? res?.data ?? null
}

export default function DocumentSeenByList({ documentId, canView }) {
  const { t } = useTranslation()
  const { isRtl } = useDirection()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [denied, setDenied] = useState(false)

  const relTime = useCallback((iso) => {
    if (!iso) return ''
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: isRtl ? arLocale : undefined })
    } catch {
      return ''
    }
  }, [isRtl])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await documentAPI.getSeenBy(documentId)
      if (res.success) {
        const data = unwrap(res)
        setEntries(Array.isArray(data) ? data : [])
      } else if (res.error?.status === 403) {
        setDenied(true)
      }
    } catch {
      setDenied(true)
    } finally {
      setLoading(false)
    }
  }, [documentId])

  useEffect(() => {
    if (canView) load()
    else { setDenied(true); setLoading(false) }
  }, [canView, load])

  // Hide the whole panel when not permitted — it's a sensitive audit list.
  if (denied || !canView) return null

  return (
    <Card padding="md">
      <Card.Header
        title={
          <span className="inline-flex items-center gap-2">
            <HiOutlineUsers className="w-5 h-5 text-brand" />
            {t('documents.detail.seenBy.title')}
            {entries.length > 0 && (
              <span className="text-xs font-normal text-ink-muted">({entries.length})</span>
            )}
          </span>
        }
        subtitle={t('documents.detail.seenBy.subtitle')}
      />

      {loading ? (
        <p className="text-sm text-ink-muted py-3 text-center">{t('common.loading')}</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-ink-muted py-4 text-center">{t('documents.detail.seenBy.empty')}</p>
      ) : (
        <ul className="space-y-2.5">
          {entries.map((e) => (
            <li key={e.user_id} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-brand-soft text-brand flex items-center justify-center text-xs font-semibold flex-shrink-0">
                {initials(e.user_name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink-primary truncate">{e.user_name}</p>
                <p className="text-[11px] text-ink-muted">{relTime(e.last_seen_at)}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {e.view_count > 0 && (
                  <Badge tone="neutral" size="sm">
                    <HiOutlineEye className="w-3.5 h-3.5" />
                    {e.view_count}
                  </Badge>
                )}
                {e.download_count > 0 && (
                  <Badge tone="info" size="sm">
                    <HiOutlineArrowDownTray className="w-3.5 h-3.5" />
                    {e.download_count}
                  </Badge>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
